import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  createIdempotencyKey,
  currentForecastMonth,
  describeForecastError,
  forecastEditState,
  mergeForecastEdits,
  normalizeForecastPayload,
  toForecastMonthDate,
  withLayoutForecastEdit,
  getLayoutForecastEditState,
  assertForecastSaved,
  withMonthlyMaterialEdit,
} from "./rsoSalesForecastHelpers";
import {getMonthlyMaterialState,materialUnitLabel,seedMonthlyMaterials} from "./rsoMaterialForecastHelpers";
import RsoCurrentMaterial from "./RsoCurrentMaterial";
import RsoPropertyForecast from "./RsoPropertyForecast";
import RsoRequestHistory from "./RsoRequestHistory";
import RsoMaterialStandards from "./RsoMaterialStandards";
import {useRsoMaterialStock} from "./useRsoMaterialStock";
import RsoChangeHistory, {ForecastComparison} from "./RsoChangeHistory";
import {beforeForecastSnapshot,draftForecastSnapshot} from "./rsoHistoryHelpers";
import "./RsoSalesForecast.css";
import {readForecastDraft, writeForecastDraft} from "./rsoForecastDraftCache";
import useRsoForecastWindow from "./useRsoForecastWindow";
import useRsoWorkspace from "./useRsoWorkspace";
import useRsoSwitch from "./useRsoSwitch";
import RsoTeamInputStatus from "./RsoTeamInputStatus";
import RsoOverrideHistory from "./RsoOverrideHistory";
import RsoSignInExpired from "./RsoSignInExpired";
import {defaultWindowMonth, isInputWeek, isWritableMonth, monthName, normalizeTotals, shortDate, tileMonth,
  tileSummary} from "./rsoForecastWindowHelpers";
import {
  loadAllForecastPages, getForecastCounts, filterForecastProperties,
  getForecastSetupMessage, getForecastSetupDetail, getForecastStatus,
  isForecastPropertyEligible, isVisibleSavedManualException,
} from "./rsoForecastUxHelpers";

const PAGE_SIZE = 10;

const EMPTY_PAYLOAD = {
  forecastMonth: "",
  salespersonName: "",
  page: 1,
  pageSize: PAGE_SIZE,
  totalCount: 0,
  totalPages: 1,
  properties: [],
  addableProperties: [],
};

const formatSubmittedOn = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const formatQuantity = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat(undefined,{maximumFractionDigits:4}).format(number);
};

const formatApartmentCount = (value) => `${Number(value || 0).toLocaleString()} ${Number(value) === 1 ? "unit" : "units"}`;

const countMaterials = (property) =>
  property.layouts.reduce((count, layout) => count + layout.materials.length, 0);

const monthLabel = (month) => toForecastMonthDate(month)
  ? new Date(`${month}-01T12:00:00`).toLocaleDateString(undefined, {month:"long", year:"numeric"}) : "a month";
const hasLegacyRecord = (property) => property.forecastUnits != null || !!property.submittedOn || property.materialForecasts != null;
const isDirtyEdit = (edit) => !!edit && String(edit.value) !== String(edit.originalValue);
// Native number inputs report an empty value for malformed text such as "1e".
// Keep that distinct from a deliberate blank; only real blanks mean zero.
const countInputValue = (control) => control.validity?.badInput ? "NaN" : control.value;
// Keep an untouched count semantically blank so merely opening a property never
// creates a draft, but show the user an unmistakable zero instead of an empty box.
const countDisplayValue = (value) => value === "NaN" ? "" : String(value ?? "").trim() === "" ? "0" : value;
const materialNamePart = (value) => typeof value === "string" && value.trim()
  && !/^(null|undefined)$/i.test(value.trim()) ? value.trim() : "";
const materialName = (material) => materialNamePart(material.privateName)
  || [material.manufacturer,material.style,material.color].map(materialNamePart).filter(Boolean).join(" ")
  || "Material name unavailable";

const ForecastError = ({ error }) =>
  error ? (
    <Alert variant="danger" className={`rsf-alert rsf-alert--${error.kind}`}>
      <Alert.Heading>{error.title}</Alert.Heading>
      <p className="mb-0">{error.message}</p>
    </Alert>
  ) : null;

const CurrentFloorplanMaterials = ({layout,materials,propertyName,manualException,id}) => (
  <section id={id} className="rsf-current-materials" aria-label={`Current materials for ${propertyName}, ${layout.floorplanName}`}>
    <span className="rsf-current-materials-label">Current {materials.length === 1 ? "material" : "materials"}</span>
    {materials.length > 0 ? <ul>{materials.map((material,index) => <li key={`${material.productId ?? "material"}-${index}`}>
      {material.productStructure && <span className="rsf-current-material-category">{material.productStructure}: </span>}
      {materialName(material)}
    </li>)}</ul> : <p>Current material details are unavailable.</p>}
  </section>
);

const MaterialRows = ({ layout, manualException = false }) => (
  <section className="rsf-layout">
    <h6 className="rsf-layout-title">{layout.floorplanName}</h6>
    <p className="rsf-muted">{manualException
      ? "These quantities come from the property's current flooring setup and are used only to calculate this month's estimate. They are not being marked as RSO."
      : "These quantities are for one unit, not the monthly total. This forecast uses only materials marked as Recurring Special Order (RSO)."}</p>
    {layout.materials.length === 0 ? (
      <p className="rsf-muted mb-0">No material details are available for this floorplan.</p>
    ) : (
      <div className="rsf-material-scroll">
        <Table bordered hover size="sm" className="rsf-material-table mb-0">
          <thead>
            <tr>
              <th>Product Structure</th>
              <th>Product Store</th>
              <th>Material</th>
              <th className="rsf-number">Per unit</th>
              <th>Unit (SF = sq ft)</th>
            </tr>
          </thead>
          <tbody>
            {layout.materials.map((material, index) => (
              <tr key={`${material.productId ?? "material"}-${index}`}>
                <td>{material.productStructure || "—"}</td>
                <td>{material.productStore || "—"}</td>
                <td>{materialName(material)}</td>
                <td className="rsf-number">{formatQuantity(material.quantity)}</td>
                <td>{material.uom || ""}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )}
  </section>
);

const PropertyChoice = ({property, edit, label, onChoose, disabled}) => {
  const status = getForecastStatus(property);
  const dirty = isDirtyEdit(edit);
  const action = !property.isEditable ? property.isPersistedManualException ? "View saved forecast" : "View RSO details"
    : dirty ? "Continue draft" : status.isSaved ? "Review saved forecast" : "Enter units";
  return <article className={`rsf-property rsf-property-choice${property.isEditable ? "" : " rsf-property-setup"}`}>
    <div className="rsf-choice-identity">
      <h5>{property.propertyName}</h5>
      <p>{property.store || "No store"} · {property.layouts.length} {property.layouts.length === 1 ? "unit type" : "unit types"}
        {label !== property.propertyName && <span> · NANCY ID {property.contactInfoId}</span>}</p>
      <span className={property.isEditable ? "rsf-choice-status" : "rsf-setup-badge"}>
        {dirty ? property.isManualException ? "Added for this month · unsaved" : "Unsaved changes" : status.isSaved ? property.materialForecasts != null ? "Saved: selected RSO flooring material" : `Saved: ${formatApartmentCount(property.forecastUnits)} needing RSO materials` : property.isManualException ? "Added for this month only" : status.kind === "ready" ? "Not entered for this month" : status.label}
      </span>
      {status.kind === "not_applicable" && <p>No RSO forecast needed. No setup changes are needed.</p>}
    </div>
    <Button variant={property.isEditable ? "outline-primary" : "outline-secondary"} disabled={disabled}
      aria-label={`${action} for ${label}`} onClick={() => onChoose(property.contactInfoId)}>{action}</Button>
  </article>;
};

const AddPropertyChoice = ({property,label,month,onAdd,disabled}) => <article className="rsf-property rsf-property-choice">
  <div className="rsf-choice-identity">
    <h5>{property.propertyName}</h5>
    <p>{property.store || "No store"} · {property.layouts.length} {property.layouts.length === 1 ? "unit type" : "unit types"}
      {label !== property.propertyName && <span> · NANCY ID {property.contactInfoId}</span>}</p>
    <span className="rsf-choice-status">Assigned to you · not normally in the RSO list</span>
  </div>
  <Button variant="outline-primary" disabled={disabled}
    aria-label={`Add ${label} for ${monthLabel(month)} only`} onClick={() => onAdd(property)}>Add for {monthLabel(month)}</Button>
</article>;

const MonthlyMaterials = ({property,counts,state:storedState,onChange,onCalculated,onChangeStandard,disabled,month,token,reloadKey}) => {
  const [remapRows,setRemapRows] = useState(null);
  const state = remapRows ? getMonthlyMaterialState(property,remapRows,counts) : storedState;
  const [editing,setEditing] = useState(() => property.isManualException === true && !state.valid && !state.needsSourceSelections);
  const stock = useRsoMaterialStock({enabled:editing,token,month,contactInfoId:property.contactInfoId,
    sourceProductIds:state.rows.map((row) => row.sourceProductId),reloadKey});
  const changeButtonRef = useRef(null);
  const wasEditing = useRef(editing);
  useEffect(() => {
    if (wasEditing.current && !editing) changeButtonRef.current?.focus();
    wasEditing.current = editing;
  },[editing]);
  const canEdit = state.canRemap || (state.valid && state.rows.length > 0 && state.rows.some((row) =>
    state.options.some((option) => option.uom === row.uom && Number(option.productId) !== Number(row.productId))));
  const changeRow = (index,changes) => {
    const rows = state.rows.map((row,rowIndex) => rowIndex === index ? {...row,...changes} : row);
    setRemapRows(null);
    onChange(rows);
  };
  const startEditing = () => {
    if (state.needsSourceSelections) {
      // Opening the chooser alone creates no draft. Choices start blank, one
      // per current source, instead of guessing how an old aggregate was split.
      setRemapRows(seedMonthlyMaterials(property,counts).map((row) => ({...row,productId:""})));
    }
    setEditing(true);
  };
  const rowLabel = (index) => state.rows.length === 1 ? "Flooring material" : `Flooring material ${index + 1}`;
  const choiceLabel = (index) => state.rows.length === 1 ? "Change to" : `Change material ${index + 1} to`;
  const totalHeading = state.needsSourceSelections ? `Saved material for ${monthLabel(month)}`
    : counts.complete ? `Material needed for ${formatApartmentCount(counts.total)}` : "Material needed";
  const amountLabel = counts.complete ? `Amount needed for ${formatApartmentCount(counts.total)}` : "Amount needed";
  const totalForRow = (row) => state.custom && !row.sourceValid && !state.needsSourceSelections ? "Unavailable" : formatQuantity(row.parsed?.value ?? row.quantity) || "Unavailable";
  const materialError = state.needsSourceSelections || remapRows ? "" : property.isManualException && !state.valid
    ? "Choose an available RSO material for every row before reviewing this property."
    : state.errorMessage;
  const monthlyOnlyLabel = `Use a different material for ${monthLabel(month)} only`;
  return <section className="rsf-materials" aria-label={`Material needed for ${property.propertyName}`}>
    <div className="rsf-materials-heading">
      <h6>{editing ? property.isManualException ? "Choose RSO material" : `Material for ${monthLabel(month)} only` : totalHeading}</h6>
      {editing
        ? <Button variant="outline-primary" disabled={disabled || !state.valid} onClick={() => setEditing(false)}>Done</Button>
        : property.isManualException || !onChangeStandard
          ? <Button ref={changeButtonRef} variant="outline-primary" disabled={disabled || !canEdit || (!state.custom && !counts.complete)} onClick={startEditing}>Change material</Button>
          : <Button ref={changeButtonRef} variant="outline-primary" disabled={disabled} onClick={() => onChangeStandard(property.contactInfoId)}>Change RSO material</Button>}
    </div>
    {!editing && !property.isManualException && !state.needsSourceSelections && <p className="rsf-materials-help">
      Keeping this material? No changes needed.
    </p>}
    <p className="rsf-materials-help">For {monthLabel(month)} only.</p>
    {editing ? <>
      {stock.dataStatus === "not_configured" && <p className="rsf-materials-note">Stock connection is not configured.</p>}
      {state.rows.map((row,index) => {
        const matchingOptions = state.options.filter((option) => option.uom === row.uom &&
          (!property.isManualException || String(option.productStructure || "").trim().toLowerCase() ===
            String(row.sourceProductStructure || "").trim().toLowerCase()));
        const total = totalForRow(row);
        const unitLabel = materialUnitLabel(row.uom) || "Unit unavailable";
        const materialId = `rsf-material-${property.contactInfoId}-${index}`;
        return <div key={index} className="rsf-material-edit-row">
          <RsoCurrentMaterial sourceProductId={row.sourceProductId} property={property} stock={stock} />
          <Form.Group controlId={materialId} className="rsf-material-select">
            <Form.Label>{choiceLabel(index)}</Form.Label>
            <Form.Select aria-label={`${choiceLabel(index)}, ${property.propertyName}`} value={row.productId}
              disabled={disabled} isInvalid={state.custom && (!row.productValid || row.duplicate)}
              aria-invalid={!!(state.custom && (!row.productValid || row.duplicate))}
              onChange={(event) => {
                const option = matchingOptions.find((item) => item.productId === Number(event.target.value));
                changeRow(index,{productId:option?.productId || "",uom:option?.uom || row.uom});
              }}>
              <option value="">{property.isManualException ? "Choose RSO material" : "Choose material"}</option>
              {matchingOptions.map((option) => <option key={option.productId} value={option.productId}>{option.productName}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="rsf-material-total">
            <span className="rsf-material-total-label">{amountLabel}</span>
            <output className="rsf-material-total-value" aria-live="polite"
              aria-label={`Amount needed for ${row.productName}, ${property.propertyName}`}>
              <strong>{total}</strong>
              {total !== "Unavailable" && <span>{unitLabel}</span>}
            </output>
          </div>
        </div>;
      })}
    </> : <>
      {state.rows.length > 0 && <div className="rsf-material-results">{state.rows.map((row,index) => {
        const total = totalForRow(row);
        return <div className="rsf-material-result" key={`${row.productId}-${index}`}>
          <div className="rsf-material-name"><span>{rowLabel(index)}</span><strong>{row.productName}</strong></div>
          <div className="rsf-material-total">
            <span className="rsf-material-total-label">{state.needsSourceSelections ? "Saved amount" : "Amount needed"}</span>
            <output className="rsf-material-total-value" aria-live="polite"
              aria-label={`${state.needsSourceSelections ? "Saved amount" : "Amount needed"} for ${row.productName}, ${property.propertyName}`}>
              <strong>{total}</strong>
              {total !== "Unavailable" && <span>{materialUnitLabel(row.uom)}</span>}
            </output>
          </div>
        </div>;
      })}</div>}
      {state.estimateError && <p className="small">{state.estimateError}</p>}
      {state.options.length === 0 && <p className="small mt-2 mb-0">No RSO materials are available for this property&apos;s store.</p>}
      {state.needsSourceSelections && <p className="rsf-materials-note">This is the saved amount. To change the material or unit counts, choose Change material, then choose what to use for each item shown.</p>}
    </>}
    {materialError && <p className="text-danger small mt-2" role="alert">{materialError}</p>}
    {!editing && !property.isManualException && onChangeStandard && canEdit && <div className="mt-2">
      <Button size="sm" variant="link" disabled={disabled || (!state.custom && !counts.complete)} onClick={startEditing}>{monthlyOnlyLabel}</Button>
    </div>}
    {property.isManualException && state.custom && state.rows.some((row) => !row.sourceValid) && counts.complete &&
      <Button size="sm" variant="outline-primary" disabled={disabled} onClick={() => {
        onChange(seedMonthlyMaterials(property,counts));
        setEditing(true);
      }}>Choose RSO materials again</Button>}
    {state.custom && !property.isManualException && <div className="mt-2"><Button size="sm" variant="link" disabled={disabled} onClick={onCalculated}>Keep current material</Button></div>}
    <details className="rsf-calculation-details">
      <summary>How is this figured?</summary>
      <p>{state.needsSourceSelections
        ? "This amount was saved with an older forecast. Its original material choices were not recorded. Choose Change material to make a new estimate from the current unit counts."
        : "We estimate the amount from your unit counts and the property's floorplan measurements. To change the amount, change the unit counts above."}</p>
    </details>
    <p className="rsf-materials-note">Planning only. No order is placed.</p>
  </section>;
};

const SavedManualForecast = ({property,month,historical=false}) => {
  const layoutRows = property.layouts || [];
  const savedTotal = property.forecastUnits != null && Number.isInteger(Number(property.forecastUnits)) ? Number(property.forecastUnits)
    : layoutRows.reduce((sum,layout) => sum + (layout.forecastUnits != null && Number.isInteger(Number(layout.forecastUnits)) ? Number(layout.forecastUnits) : 0),0);
  const options = property.materialOptions || [];
  const savedMaterials = Array.isArray(property.materialForecasts) ? property.materialForecasts : [];
  return <section className="rsf-saved-manual" aria-label={`Saved one-month forecast for ${property.propertyName}`}>
    <p className="rsf-manual-exception-note">{historical ? <>Saved for {monthLabel(month)} using the previous calculation method. This forecast is view only.</> : <><strong>Saved for {monthLabel(month)} only.</strong> This does not permanently mark the property or its flooring as RSO. The saved forecast is view only.</>}</p>
    <section className="rsf-apartment-summary" aria-label={`Saved unit summary for ${property.propertyName}`}>
      <span>Units saved this month</span>
      <output>{formatApartmentCount(savedTotal)}</output>
      {!historical && <p>These saved counts cannot be changed until the RSO material choices are updated.</p>}
    </section>
    {layoutRows.length > 0 && <Table size="sm" className="mb-3"><thead><tr><th>Unit type</th><th className="rsf-number">Saved units</th></tr></thead>
      <tbody>{layoutRows.map((layout,index) => <tr key={`${layout.unitTypeId}-${index}`}><td>{layout.floorplanName}</td>
        <td className="rsf-number">{layout.forecastUnits != null && Number.isInteger(Number(layout.forecastUnits)) ? Number(layout.forecastUnits) : "—"}</td></tr>)}</tbody>
      <tfoot><tr><th>Total</th><th className="rsf-number">{savedTotal}</th></tr></tfoot></Table>}
    <h6>Saved RSO material totals</h6>
    {savedMaterials.length > 0 ? <ul>{savedMaterials.map((material,index) => {
      const option = options.find((item) => Number(item.productId) === Number(material.productId));
      return <li key={`${material.productId}-${index}`}>{option?.productName || "Saved RSO material"} · {formatQuantity(material.quantity)} {materialUnitLabel(material.uom)}</li>;
    })}</ul> : <p className="small">No saved material totals are available.</p>}
  </section>;
};

const PropertyForecast = ({ property, edit, month, token, reloadKey, onChange, onZero, onUndo, onMaterialChange, onCalculated, onChangeStandard, submitting }) => {
  const summary = getLayoutForecastEditState(property, edit);
  const materials = getMonthlyMaterialState(property,edit?.materialForecasts === undefined ? property.materialForecasts : edit.materialForecasts,summary);
  const isDirty = summary.dirty;
  const editable = property.isEditable && property.contactInfoId !== null;
  const setupMessage = getForecastSetupMessage(property);
  const status = getForecastStatus(property);
  const setupDetail = getForecastSetupDetail(property) || (status.kind === "review" ? status.description : "");
  const ready = editable && !setupMessage;
  const lockedManual = isVisibleSavedManualException(property) && !ready;
  const submittedOn = formatSubmittedOn(property.submittedOn);
  const layoutCount = property.layouts.length;
  const materialCount = countMaterials(property);

  return (
    <article className={"rsf-property" + (ready ? "" : " rsf-property-setup")}>
      <div className="rsf-property-header">
        <div className="rsf-property-identity">
          <h5 tabIndex="-1" data-rsf-editor-heading>{property.propertyName}</h5>
          <div className="rsf-property-meta">
            <span>{property.store || "No store"}</span>
            {property.costarId && <span>CoStar {property.costarId}</span>}
          </div>
          {!ready && <p className="rsf-setup-reason">{lockedManual ? setupDetail || setupMessage : setupMessage || "Ask your support team whether this property needs an RSO forecast."}</p>}
          {setupDetail && !lockedManual && <p className="rsf-setup-reason">{setupDetail}</p>}
        </div>

        {ready ? <div className="rsf-property-status" aria-live="polite">
          <span>{isDirty ? "Unsaved changes" : status.kind === "ready" ? "No forecast entered" : status.label}</span>
          {!isDirty && submittedOn && <div className="rsf-saved-date">{submittedOn}</div>}
        </div> : <span className="rsf-setup-badge">{status.label}</span>}
      </div>

      {lockedManual && <SavedManualForecast property={property} month={month} />}

      {ready && <div className="rsf-floorplan-entry">
        {property.isManualException && <p className="rsf-manual-exception-note"><strong>Added for {monthLabel(month)} only.</strong> This does not permanently mark this property or its flooring as RSO. Choose the RSO material for this forecast before reviewing it.</p>}
        <section className="rsf-apartment-summary" aria-label={`Unit summary for ${property.propertyName}`}>
          <span>Total RSO units</span>
          <output aria-live="polite" aria-label={`Total RSO units for ${property.propertyName}`}>
            {summary.complete ? formatApartmentCount(summary.total) : "Check the unit counts"}
          </output>
        </section>
        {status.isSaved && !isDirty && <p className="rsf-already-saved">Saved for {monthLabel(month)}. Update only if your estimate changed.</p>}
        <h6 className="rsf-floorplan-title">Units by floor plan</h6>
        <p className="rsf-floorplan-help">Enter units—not square feet. Use 0 if none.</p>
        <p className="rsf-floorplan-help">Materials shown are from the property&apos;s current setup. Changes here apply only to this forecast.</p>
        {!property.isManualException && property.excludedNonRsoLayoutCount > 0 && <p className="rsf-entry-rule">Only unit types that use RSO materials are shown. {property.excludedNonRsoLayoutCount} non-RSO unit {property.excludedNonRsoLayoutCount === 1 ? "type is" : "types are"} not included.</p>}
        {summary.layouts.map((layout) => {
          const id = `rsf-units-${property.contactInfoId}-${layout.unitTypeId}`;
          const displayValue = countDisplayValue(layout.value);
          // Count drafts carry layout identity, not the current setup's material rows.
          const currentMaterials = property.layouts.find((item) => Number(item.unitTypeId) === Number(layout.unitTypeId))?.materials || [];
          return <Form.Group key={layout.unitTypeId} controlId={id} className="rsf-layout-input-row">
            <div className="rsf-layout-identity">
              <Form.Label>{layout.floorplanName}</Form.Label>
              <CurrentFloorplanMaterials layout={layout} materials={currentMaterials} propertyName={property.propertyName}
                manualException={property.isManualException} id={`${id}-current-materials`} />
            </div>
            <div className="rsf-layout-count">
              <Form.Control type="number" inputMode="numeric" min="0" max="2147483647" step="1"
                value={displayValue} disabled={submitting}
                isInvalid={isDirty && layout.invalid}
                aria-invalid={!!(isDirty && layout.invalid)}
                aria-label={`Units needing RSO materials for ${property.propertyName}, ${layout.floorplanName}`}
                aria-describedby={`${id}-hint ${id}-current-materials${isDirty && layout.invalid ? ` ${id}-error` : ""}`}
                onFocus={(event) => { if (displayValue === "0") event.currentTarget.select(); }}
                onChange={(event) => onChange(property, layout.unitTypeId, countInputValue(event.target))}
                onInput={(event) => {
                  if (event.target.validity?.badInput || layout.value === "NaN") {
                    onChange(property, layout.unitTypeId, countInputValue(event.target));
                  }
                }} />
              <span id={`${id}-hint`} className="rsf-count-unit">units</span>
              <Form.Control.Feedback type="invalid" id={`${id}-error`}>Use a whole number of 0 or more.</Form.Control.Feedback>
            </div>
          </Form.Group>;
        })}
        <div className="rsf-property-total" aria-live="polite">
          <strong>{summary.complete && (isDirty || summary.answeredCount > 0) ? `Total: ${formatApartmentCount(summary.total)}` : summary.answeredCount ? `Units entered: ${summary.total ?? "—"}` : "No forecast entered"}</strong>
          {!summary.complete && isDirty && <span>{summary.errorMessage || "Check the highlighted counts before reviewing this property."}</span>}
        </div>
        <div className="rsf-property-actions">
          <Button size="sm" variant="outline-secondary" disabled={submitting} onClick={() => onZero(property)}>{materials.custom ? "Set unit counts to 0" : !status.isSaved && !isDirty && summary.answeredCount === 0 ? "No RSO units this month" : "Set every count to 0"}</Button>
          {isDirty && <Button size="sm" variant="link" disabled={submitting} onClick={() => onUndo(property)}>{property.isLocallyAdded ? "Remove added property" : "Undo changes for this property"}</Button>}
        </div>
        {materials.custom && <p className="small mb-0">Setting unit counts to 0 keeps the selected flooring material and updates its estimate.</p>}
      </div>}
      {ready && <MonthlyMaterials property={property} counts={summary} state={materials} disabled={submitting}
        month={month} token={token} reloadKey={reloadKey}
        onChange={(rows) => onMaterialChange(property,rows)} onCalculated={() => onCalculated(property)}
        onChangeStandard={onChangeStandard} />}
      {ready && isDirty && summary.complete && materials.valid && <ForecastComparison
        heading={`Your changes for ${monthLabel(month)}`} before={beforeForecastSnapshot(property)}
        after={draftForecastSnapshot(property,summary,materials)} />}
      <RsoChangeHistory key={`${property.contactInfoId}-${month}`} property={property} month={month} token={token} reloadKey={reloadKey} />

      <details className="rsf-details">
        <summary>
          View layout &amp; materials · {" "}
          {layoutCount} {layoutCount === 1 ? "floorplan" : "floorplans"} · {materialCount}{" "}
          {materialCount === 1 ? "material" : "materials"}
        </summary>
        <div className="rsf-layouts">
          {layoutCount === 0 ? (
            <p className="rsf-muted mb-0">No floorplan or material details are available.</p>
          ) : (
            property.layouts.map((layout, index) => (
              <MaterialRows
                key={`${layout.unitTypeId ?? layout.floorplanName}-${index}`}
                layout={layout}
                manualException={property.isManualException}
              />
            ))
          )}
        </div>
      </details>
    </article>
  );
};

const materialRowsForReview = (materials) => materials?.custom && Array.isArray(materials.materialForecasts)
  ? materials.materialForecasts.map((row) => ({...row,parsed:{value:row.quantity},
    productName:materials.options.find((option) => Number(option.productId) === Number(row.productId))?.productName || "RSO material"}))
  : materials?.rows || [];

const ForecastReview = ({items, month, saved = false}) => <section className="rsf-review" aria-label={saved ? "Saved forecast details" : "Forecast review"}>
  <h4 tabIndex="-1">{saved ? "Your RSO forecast is saved" : "Check your RSO forecast before saving"}</h4>
  <p><strong>{new Date(month + "-01T12:00:00").toLocaleDateString(undefined, {month:"long", year:"numeric"})}</strong> · {items.length} {items.length === 1 ? "property" : "properties"} · {formatApartmentCount(items.reduce((sum,item) => sum + item.total,0))}</p>
  <p>{saved ? "These counts are saved for planning RSO materials only, not other flooring work. No purchase order was placed." : "These counts must include only units needing RSO materials, not other flooring work. Every unit type is shown, including those with 0 units. Check each unit type and the month. Only the properties below will change; saving does not place an order."}</p>
  {items.map((item) => <article key={item.contactInfoId} className="rsf-review-property">
    <h5>{item.displayLabel || item.propertyName}</h5>
    {item.isManualException && <p><strong>Added for {monthLabel(month)} only.</strong> This does not permanently change the property&apos;s RSO setting.</p>}
    {!saved && item.replacesUnsplitTotal && <p>These counts will replace the previously saved total. No old unit-type split was assumed.</p>}
    {!saved && item.beforeSnapshot && <ForecastComparison heading={`Your changes for ${monthLabel(month)}`}
      before={item.beforeSnapshot} after={item.afterSnapshot} />}
    <Table size="sm" className="mb-0"><thead><tr><th>Unit type</th><th className="rsf-number">Units</th></tr></thead>
      <tbody>{item.layouts.map((layout) => <tr key={layout.unitTypeId}><td>{layout.floorplanName}</td><td className="rsf-number">{layout.forecastUnits ?? layout.parsed.value ?? 0}</td></tr>)}</tbody>
      <tfoot><tr><th>Total</th><th className="rsf-number">{item.total}</th></tr></tfoot>
    </Table>
    {item.materials && <section className="mt-3" aria-label={`Material needed for ${item.displayLabel || item.propertyName}`}>
      <h6>Material needed for {formatApartmentCount(item.total)}</h6>
      <p className="small mb-1">Calculated from the unit counts above.</p>
      {materialRowsForReview(item.materials).length > 0 ? <ul>{materialRowsForReview(item.materials).map((row,index) => <li key={`${row.productId}-${index}`}>
        {row.productName} · {formatQuantity(row.parsed?.value ?? row.quantity)} {materialUnitLabel(row.uom)}
      </li>)}</ul> : <p className="small">The material estimate is not available here.</p>}
      <p className="small mb-0">This estimate does not change prices or place an order.</p>
    </section>}
  </article>)}
</section>;

// Input-week dashboard tile. Counts come from the server's workspace totals, times from its window.
// totalsStatus: "loading" until the first count read finishes, then "ready" or "failed" (e.g. a sales manager
// with no RSO properties of their own). A failed read is said plainly; it never stays on "loading".
// forbidden: the account has no RSO properties of its own (403), e.g. a sales manager; retrying won't change that.
// signInExpired: NANCY refused a read because the sign-in expired; the tile shows no counts and the sign-in message.
const InputWeekTile = ({entryWindow, totals, totalsStatus, forbidden, forbiddenMessage, canViewTeamStatus, signInExpired, onOpen, onRetry}) => {
  const summary = tileSummary(entryWindow, totals);
  const valid = normalizeTotals(totals);
  const failed = totalsStatus === "failed" && !valid;
  const open = summary.tone === "open" || summary.tone === "due-today";
  // During the week, properties still left make a red glow pulse behind the (still green) circle; faster on the last day.
  const needsInput = open && !!valid && valid.left > 0;
  const value = signInExpired ? "—" : summary.tone === "complete" ? "Done" : summary.tone === "none" ? "0"
    : open ? valid ? String(valid.left) : failed ? "—" : "…" : "Closed";
  const caption = signInExpired ? "sign-in expired" : summary.tone === "complete" ? `${valid.done} of ${valid.required} done`
    : summary.tone === "none" ? "properties to forecast"
      : open ? valid ? `of ${valid.required} left` : failed ? forbidden ? "no RSO properties" : "not available" : "loading" : "view only";
  const unavailable = forbidden
    ? canViewTeamStatus ? "Your account has no RSO properties to forecast. Open this to see Team input status."
      : forbiddenMessage || "Your account has no RSO properties to forecast."
    : `Your RSO checklist could not be loaded. ${canViewTeamStatus ? "Open this to see Team input status." : "Try again later."}`;
  return <>
    <div className={`rsf-tile${needsInput ? ` rsf-tile--needs-input${summary.tone === "due-today" ? " rsf-tile--due-today" : ""}` : ""}`}>
      <button type="button" className={`rsf-button ${summary.tone === "closed-missing" ? "rsf-unknown" : "rsf-calm"}`}
        onClick={onOpen} title="Open your RSO forecast checklist">
        <span className="rsf-label">{monthName(summary.month)} RSO forecast</span>
        <span className="rsf-value">{value}</span>
        <span className="rsf-caption">{caption}</span>
        <span className="rsf-subcount">Open RSO checklist →</span>
      </button>
    </div>
    {signInExpired ? <RsoSignInExpired role="status" className="rsf-window-status" />
      : <p className={`rsf-window-status rsf-window-status--${failed ? "failed" : summary.tone}`} role="status">
      {failed ? <>{unavailable}{open ? "" : ` ${summary.detail}`}</> : <>
        {summary.headline && <strong>{summary.headline}</strong>}{summary.headline && summary.detail ? " " : ""}{summary.detail}
        {summary.unsent && <span className="rsf-window-unsent"> · {summary.unsent}</span>}</>}
    </p>}
    {failed && !forbidden && !signInExpired && <Button variant="link" size="sm" className="rsf-window-retry" onClick={onRetry}>Try again</Button>}
  </>;
};

const ForecastSession = ({ token, legacyCompatibility = false }) => {
  const [standardsDirty,setStandardsDirty] = useState(false);
  const [standardsBusy,setStandardsBusy] = useState(false);
  const [standardsClosePending,setStandardsClosePending] = useState(false);
  const [standardsSession,setStandardsSession] = useState(0);
  const [standardsNavigation,setStandardsNavigation] = useState(null);
  const [standardsMaterialAction,setStandardsMaterialAction] = useState(null);
  const standardsSessionActions = useRef(null);
  const draftNavigationLock = useRef(false);
  const [draftNavigationBusy,setDraftNavigationBusy] = useState(false);
  const [draftNavigationError,setDraftNavigationError] = useState("");
  const handleSessionActions = useCallback((actions) => {standardsSessionActions.current=actions;},[]);
  const [standardsPropertyId,setStandardsPropertyId] = useState(null);
  const [restoredDraft] = useState(() => readForecastDraft(token));
  const [workflow,setWorkflow] = useState(restoredDraft ? "monthly" : "standards");
  const legacyDraftIds = useMemo(() => new Set(Object.keys(restoredDraft?.edits || {})),[restoredDraft]);
  const [standardsEnabled,setStandardsEnabled] = useState(true);
  const handleStandardsAvailability = useCallback((enabled) => {
    if (!legacyCompatibility) return;
    setStandardsEnabled(enabled);if (!enabled) setWorkflow("monthly");
  },[legacyCompatibility]);
  const StandardsTool = legacyCompatibility ? RsoMaterialStandards : RsoPropertyForecast;
  // The RSO input week (null when switched off; the retained legacy editor never reads it).
  const forecastWindow = useRsoForecastWindow({token, enabled: !legacyCompatibility});
  const entryWindow = forecastWindow.window;
  const windowMonth = entryWindow ? defaultWindowMonth(entryWindow) : "";
  // Overrides (sending outside the input week with a sign-off) are listed in their own tab while they are allowed.
  const showOverrides = !legacyCompatibility && !!entryWindow && forecastWindow.overrideEnabled;

  const [showDetails, setShowDetails] = useState(!!restoredDraft);
  const [month, setMonth] = useState(() => restoredDraft?.month || currentForecastMonth());
  const [monthChoice, setMonthChoice] = useState(() => restoredDraft?.month || currentForecastMonth());
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [addingProperty, setAddingProperty] = useState(false);
  const [addedProperties, setAddedProperties] = useState({});
  const [stage, setStage] = useState(restoredDraft ? "edit" : "month");
  const [resolvedWindowMonth, setResolvedWindowMonth] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [savedReview, setSavedReview] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [payload, setPayload] = useState(null);
  const [loadStatus, setLoadStatus] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [edits, setEdits] = useState(() => restoredDraft?.edits || {});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [conflictPending,setConflictPending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const retryKey = useRef(restoredDraft?.retryKey || null);
  const bodyRef = useRef(null);
  const keepChangesRef = useRef(null);
  const focusInvalid = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);
  useEffect(() => {
    writeForecastDraft(token, {month, edits, retryKey: retryKey.current});
  }, [token, month, edits]);
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = 0;
    if (pendingAction) {
      keepChangesRef.current?.focus();
      return;
    }
    const activeBody = bodyRef.current.querySelector(workflow === "history" ? "[data-rsf-history-body]" : workflow === "team" ? "[data-rsf-team-body]"
      : workflow === "overrides" ? "[data-rsf-overrides-body]" : workflow === "monthly" ? "[data-rsf-monthly-body]" : "[data-rsf-standards-body]");
    if (workflow === "monthly" && stage === "edit" && selectedPropertyId !== null) {
      const target = focusInvalid.current
        ? activeBody?.querySelector('input[aria-invalid="true"]') || activeBody?.querySelector("[data-rsf-editor-heading]")
        : activeBody?.querySelector("[data-rsf-editor-heading]");
      target?.focus();
      focusInvalid.current = false;
    } else activeBody?.querySelector("h4")?.focus();
  }, [stage, selectedPropertyId, pendingAction, focusRequest,workflow]);

  // Resolve the input week before requesting a summary. The local calendar month
  // can differ from the month Sales is entering during the next input week.
  const summaryMonthReady = forecastWindow.status !== "loading" && resolvedWindowMonth === windowMonth;
  // The input-week tile and current property editor read their shared workspace
  // and property directory. They do not consume the legacy floor-plan summary.
  // Keep that summary for the window-off tile, legacy editor and unsaved drafts.
  const needsLegacySummary = legacyCompatibility || !entryWindow || workflow === "monthly"
    || Object.values(edits).some(isDirtyEdit);
  useEffect(() => {
    if (!summaryMonthReady) return undefined;
    if (!needsLegacySummary) {
      setPayload(null);
      setLoadError(null);
      setLoadStatus("ok");
      return undefined;
    }
    if (!token) {
      setLoadStatus("error");
      setLoadError({kind: "auth", title: "Sign in to view forecasts", message: "Your session has ended. Sign in to NANCY again."});
      return undefined;
    }
    const controller = new AbortController();
    const requestedManualIds = new Set([
      ...Object.keys(addedProperties),
      ...Object.values(edits).filter((entry) => entry?.isManualException === true)
        .map((entry) => String(entry.contactInfoId)),
    ]);
    setLoadStatus(payload ? "refreshing" : "loading");
    setLoadError(null);

    loadAllForecastPages(async (requestedPage) => {
      const response = await axios.get(`${API_URL}/Dashboard/api/Dashboard/RsoSalesForecast`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          forecastMonth: toForecastMonthDate(month),
          page: requestedPage,
          pageSize: 100,
        },
        signal: controller.signal,
        timeout: 60000,
      });
      return normalizeForecastPayload(response.data, toForecastMonthDate(month));
    })
      .then((normalized) => {
        if (controller.signal.aborted) return;
        const serverIds = new Set(normalized.properties.map((property) => String(property.contactInfoId)));
        const refreshedAdded = normalized.addableProperties
          .filter((property) => requestedManualIds.has(String(property.contactInfoId)) && !serverIds.has(String(property.contactInfoId)))
          .map((property) => ({...property,isLocallyAdded:true}));
        setPayload(normalized);
        setAddedProperties(Object.fromEntries(refreshedAdded.map((property) => [String(property.contactInfoId),property])));
        setEdits((previous) => mergeForecastEdits(previous, [...normalized.properties,...refreshedAdded]));
        setLoadStatus("ok");
      })
      .catch((error) => {
        if (controller.signal.aborted || axios.isCancel(error)) return;
        // Do not keep showing names from a list whose current authorization
        // could not be confirmed. Drafts remain cached, without being named.
        setPayload(null);
        setAddedProperties({});
        setAddingProperty(false);
        setLoadError(
          describeForecastError(error, "Could not load this month's RSO forecasts.")
        );
        setLoadStatus("error");
      });

    return () => controller.abort();
    // payload is intentionally excluded: it decides initial vs refresh styling,
    // but including it would re-fetch every successful response indefinitely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, month, reloadKey, summaryMonthReady, needsLegacySummary]);

  useEffect(() => {
    setResolvedWindowMonth(windowMonth);
    if (!windowMonth) return;
    setMonthChoice(windowMonth);
    // A restored older-month draft keeps its month until Sales discards it; the month step offers only the window month.
    if (!Object.values(edits).some(isDirtyEdit)) setMonth(windowMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowMonth]);
  const tileForMonth = entryWindow ? tileMonth(entryWindow) : "";
  const forecastWorkspace = useRsoWorkspace({token,month:tileForMonth,enabled:!!entryWindow,refreshKey:forecastWindow.version});
  const tileTotals = {...forecastWorkspace,totals:normalizeTotals(forecastWorkspace.data?.totals),
    status:forecastWorkspace.status === "ready" && !normalizeTotals(forecastWorkspace.data?.totals) ? "failed" : forecastWorkspace.status};
  // A read of the week or of the counts was refused because the sign-in expired.
  const signInExpired = forecastWindow.signInExpired || tileTotals.signInExpired === true;

  const data = payload || EMPTY_PAYLOAD;
  const serverProperties = useMemo(() => data.properties.filter((property) =>
    isForecastPropertyEligible(property) || isVisibleSavedManualException(property)), [data.properties]);
  const eligibleProperties = useMemo(() => {
    const serverIds = new Set(serverProperties.map((property) => String(property.contactInfoId)));
    return [...serverProperties,...Object.values(addedProperties).filter((property) =>
      isForecastPropertyEligible(property) && !serverIds.has(String(property.contactInfoId)))];
  }, [serverProperties,addedProperties]);
  const eligibleIds = useMemo(() => new Set(eligibleProperties.map((property) => String(property.contactInfoId))), [eligibleProperties]);
  const addableProperties = useMemo(() => data.addableProperties.filter((property) =>
    isForecastPropertyEligible(property) && !eligibleIds.has(String(property.contactInfoId))), [data.addableProperties,eligibleIds]);
  const eligibleEdits = useMemo(() => Object.fromEntries(Object.entries(edits).filter(([, entry]) =>
    eligibleIds.has(String(entry?.contactInfoId)))), [edits, eligibleIds]);
  const editState = useMemo(() => forecastEditState(eligibleEdits, eligibleProperties), [eligibleEdits, eligibleProperties]);
  const dirtyEdits = Object.values(edits).filter(isDirtyEdit);
  const eligibleDirtyEdits = dirtyEdits.filter((entry) => eligibleIds.has(String(entry.contactInfoId)));
  const unavailableChanges = dirtyEdits.filter((entry) => !eligibleIds.has(String(entry.contactInfoId)));
  const dirtyCount = dirtyEdits.length;
  const hasChanges = dirtyCount > 0;
  const counts = useMemo(() => getForecastCounts(eligibleProperties), [eligibleProperties]);
  // This tile describes the permanent RSO list. A property added only to one
  // month's forecast must not change its number while that draft is open.
  const permanentRsoPropertyCount = useMemo(() => serverProperties.filter((property) =>
    property?.isManualException !== true && String(property?.rsoScope || "").trim().toLowerCase() === "rso").length,
  [serverProperties]);
  const filtered = useMemo(() => filterForecastProperties(eligibleProperties, {query, view:"ready"}).filter((property) =>
    legacyCompatibility || hasLegacyRecord(property) || (month === restoredDraft?.month && legacyDraftIds.has(String(property.contactInfoId)))),
    [eligibleProperties, query, legacyCompatibility, month, restoredDraft, legacyDraftIds]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleProperties = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filteredCandidates = useMemo(() => filterForecastProperties(addableProperties, {query:candidateQuery,view:"ready"}), [addableProperties,candidateQuery]);
  const candidateTotalPages = Math.max(1,Math.ceil(filteredCandidates.length / PAGE_SIZE));
  const currentCandidatePage = Math.min(candidatePage,candidateTotalPages);
  const visibleCandidates = filteredCandidates.slice((currentCandidatePage - 1) * PAGE_SIZE,currentCandidatePage * PAGE_SIZE);
  const selectedProperty = selectedPropertyId === null ? null : eligibleProperties.find((property) => String(property.contactInfoId) === String(selectedPropertyId));
  const legacyEditable = legacyCompatibility || (month === restoredDraft?.month && legacyDraftIds.has(String(selectedPropertyId)) && isDirtyEdit(edits[String(selectedPropertyId)]));
  const busy = submitting || loadStatus !== "ok";
  const cannotReview = busy || unavailableChanges.length > 0 || editState.invalid.length > 0 || editState.entries.length === 0;
  const propertyLabel = (property) => eligibleProperties.filter((item) => item.propertyName.toLowerCase() === property.propertyName.toLowerCase()).length > 1
    ? `${property.propertyName} (ID ${property.contactInfoId})` : property.propertyName;
  const candidateLabel = (property) => addableProperties.filter((item) => item.propertyName.toLowerCase() === property.propertyName.toLowerCase()).length > 1
    ? `${property.propertyName} (ID ${property.contactInfoId})` : property.propertyName;
  const reviewItems = editState.dirty.map((entry) => {
    const property = eligibleProperties.find((item) => String(item.contactInfoId) === String(entry.contactInfoId));
    const counts = property ? getLayoutForecastEditState(property,entry) : null;
    const materials = property ? getMonthlyMaterialState(property,entry.materialForecasts === undefined ? property.materialForecasts : entry.materialForecasts,counts) : null;
    return property ? {contactInfoId: property.contactInfoId, propertyName: property.propertyName,
      isManualException:property.isManualException === true,
      replacesUnsplitTotal: getForecastStatus(property).kind === "review",
      displayLabel: propertyLabel(property), ...counts,
      beforeSnapshot:beforeForecastSnapshot(property),afterSnapshot:draftForecastSnapshot(property,counts,materials),materials} : null;
  }).filter(Boolean);
  useEffect(() => {
    if (!hasChanges) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasChanges]);
  const loaded = loadStatus === "ok" || loadStatus === "refreshing";
  const circleState = loadStatus === "error" ? "rsf-unknown" : "rsf-calm";

  const chooseProperty = (id, invalid = false) => {
    setSelectedPropertyId(String(id));
    setStage("edit");
    focusInvalid.current = invalid;
    setFocusRequest((value) => value + 1);
  };
  const chooseAnotherProperty = () => {
    setSelectedPropertyId(null);
    setAddingProperty(false);
    setStage("edit");
  };
  const openAddProperty = () => {
    setSelectedPropertyId(null);
    setAddingProperty(true);
    setCandidatePage(1);
    setStage("edit");
  };
  const addProperty = (candidate) => {
    if (busy || !isForecastPropertyEligible(candidate) || eligibleIds.has(String(candidate.contactInfoId))) return;
    const property = {...candidate,isLocallyAdded:true};
    const key = String(property.contactInfoId);
    setAddedProperties((previous) => ({...previous,[key]:property}));
    setEdits((previous) => {
      const merged = mergeForecastEdits(previous,[property]);
      if (isDirtyEdit(previous[key])) return merged;
      const counts = getLayoutForecastEditState(property,merged[key]);
      return withMonthlyMaterialEdit(merged,property,seedMonthlyMaterials(property,counts));
    });
    setAddingProperty(false);
    setSelectedPropertyId(key);
    setFocusRequest((value) => value + 1);
    setSubmitError(null);
    setSuccessMessage("");
  };
  const openMonthChoice = () => {
    setMonthChoice(windowMonth || month);
    setStage("month");
  };

  const changeMonth = (nextMonth) => {
    setMonth(nextMonth);
    setMonthChoice(nextMonth);
    setPage(1);
    setQuery("");
    setCandidatePage(1);
    setCandidateQuery("");
    setAddingProperty(false);
    setAddedProperties({});
    setSelectedPropertyId(null);
    setPayload(null);
    setEdits({});
    setConflictPending(false);
    setLoadError(null);
    setSubmitError(null);
    setSuccessMessage("");
    setStage("edit");
    setSavedReview([]);
    retryKey.current = null;
  };

  const flushPropertyDrafts = async () => {
    if (legacyCompatibility || !standardsSessionActions.current) return true;
    setDraftNavigationError("");
    try { return await standardsSessionActions.current.flushDrafts(); }
    catch (_) {setDraftNavigationError("Your draft could not be saved. Keep this window open and try again.");return false;}
  };
  const changeWorkflow = async (next) => {
    if (submitting || standardsBusy || draftNavigationLock.current) return;
    draftNavigationLock.current=true;setDraftNavigationBusy(true);
    try {
      if (!(await flushPropertyDrafts())) return;
      if (next === "standards") setStandardsPropertyId(null);
      setWorkflow(next);
    } finally {draftNavigationLock.current=false;setDraftNavigationBusy(false);}
  };
  // Overrides switched off while their tab is open: back to the properties.
  useEffect(() => {if (workflow === "overrides" && !showOverrides) setWorkflow("standards");}, [workflow, showOverrides]);
  const requestAction = async (action) => {
    if (submitting || standardsBusy || draftNavigationLock.current) return;
    draftNavigationLock.current=true;setDraftNavigationBusy(true);
    try {
      if (!(await flushPropertyDrafts())) return;
      const permanentDirty = legacyCompatibility ? standardsDirty : standardsSessionActions.current?.hasPendingMaterialChanges;
      if (action.kind === "close" && permanentDirty) {setStandardsClosePending(true);return;}
      if (hasChanges) { setPendingAction(action); return; }
      if (action.kind === "month") changeMonth(action.month);
      else setShowDetails(false);
    } finally {draftNavigationLock.current=false;setDraftNavigationBusy(false);}
  };
  const discardAndContinue = () => {
    const action = pendingAction;
    setPendingAction(null);
    setEdits(mergeForecastEdits({}, data.properties));
    setConflictPending(false);
    setAddedProperties({});
    setAddingProperty(false);
    retryKey.current = null;
    if (action?.kind === "month") changeMonth(action.month);
    else setShowDetails(false);
  };
  const confirmMonth = () => {
    if (!token || !toForecastMonthDate(monthChoice)) return;
    if (monthChoice !== month) requestAction({kind:"month", month:monthChoice});
    else setStage("edit");
  };
  const keepPendingChanges = () => {
    if (pendingAction?.kind === "month") {
      setMonthChoice(month);
      setStage("edit");
    }
    setPendingAction(null);
  };

  const handleForecastChange = (property, unitTypeId, value) => {
    setEdits((previous) => withLayoutForecastEdit(previous, property, unitTypeId, value));
    setSubmitError(null);
    setSuccessMessage("");
  };
  const handleMaterialChange = (property,materials) => {
    setEdits((previous) => withMonthlyMaterialEdit(previous,property,materials));
    setSubmitError(null);
    setSuccessMessage("");
  };
  const confirmCalculatedMaterials = () => {
    const property = eligibleProperties.find((item) => String(item.contactInfoId) === String(pendingAction?.contactInfoId));
    if (property?.isEditable && !busy) handleMaterialChange(property,null);
    setPendingAction(null);
  };
  const applyZero = (property) => {
    setEdits((previous) => property.layouts.reduce((next,layout) => withLayoutForecastEdit(next,property,layout.unitTypeId,"0"),previous));
    setSubmitError(null);
    setSuccessMessage("");
  };
  const handleZero = (property) => {
    const summary = getLayoutForecastEditState(property, edits[String(property.contactInfoId)]);
    if (summary.layouts.some((layout) => Number(layout.value) > 0) ||
        property.layouts.some((layout) => Number(layout.forecastUnits) > 0) || Number(property.forecastUnits) > 0) {
      setPendingAction({kind:"zero", contactInfoId:property.contactInfoId, propertyName:property.propertyName,
        hasCustomMaterials:(edits[String(property.contactInfoId)]?.materialForecasts === undefined ? property.materialForecasts : edits[String(property.contactInfoId)].materialForecasts) != null});
    } else applyZero(property);
  };
  const confirmZero = () => {
    const property = eligibleProperties.find((item) => String(item.contactInfoId) === String(pendingAction?.contactInfoId));
    if (property?.isEditable && !busy) applyZero(property);
    setPendingAction(null);
  };
  const handleUndo = (property) => {
    if (property.isLocallyAdded) {
      const key = String(property.contactInfoId);
      setEdits((previous) => {
        const next = {...previous};
        delete next[key];
        return next;
      });
      setAddedProperties((previous) => {
        const next = {...previous};
        delete next[key];
        return next;
      });
      setSelectedPropertyId(null);
      return;
    }
    setEdits((previous) => {
      const next = {...previous};
      delete next[String(property.contactInfoId)];
      return mergeForecastEdits(next,[property]);
    });
    setSubmitError(null);
    setSuccessMessage("");
  };

  const openMaterialStandards = (contactInfoId) => {
    setStandardsPropertyId(Number(contactInfoId));
    setStandardsClosePending(false);
    setWorkflow("standards");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handleSubmit = async () => {
    if (stage !== "review" || cannotReview || conflictPending) {
      return;
    }

    const forecastMonth = toForecastMonthDate(month);
    const signature = JSON.stringify({ forecastMonth, entries: editState.entries });
    if (!retryKey.current || retryKey.current.signature !== signature) {
      retryKey.current = { signature, key: createIdempotencyKey() };
    }
    writeForecastDraft(token, {month, edits, retryKey:retryKey.current});

    setSubmitting(true);
    setSubmitError(null);
    setSuccessMessage("");
    try {
      const response = await axios.post(
        `${API_URL}/Dashboard/api/Dashboard/RsoSalesForecast`,
        {
          forecastMonth,
          idempotencyKey: retryKey.current.key,
          entries: editState.entries,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (!mounted.current) return;
      const receipt = assertForecastSaved(response.data, {forecastMonth, entries: editState.entries});
      const count = editState.entries.length;
      const savedNames = reviewItems.slice(0, 3).map((entry) => entry.displayLabel || entry.propertyName).join(", ") + (count > 3 ? " and others" : "");
      const accepted = new Map(receipt.properties.map((entry) => [String(entry.contactInfoId), entry]));
      setPayload((previous) => previous && ({...previous, properties: previous.properties.map((property) => {
        const saved = accepted.get(String(property.contactInfoId));
        return saved ? {...property, forecastUnits: saved.forecastUnits,
          materialForecasts:saved.materialForecasts ?? null,
          ...(saved.forecastRevision !== undefined ? {forecastRevision:saved.forecastRevision,
            materialSelections:saved.materialSelections ?? null,savedSnapshot:saved.savedSnapshot} : {}),
          layouts: property.layouts.map((layout) => ({...layout,forecastUnits:saved.layoutForecasts.find((item) => Number(item.unitTypeId) === Number(layout.unitTypeId)).forecastUnits})),
          isEditable: typeof saved.isEditable === "boolean" ? saved.isEditable : property.isEditable,
          submittedOn: typeof saved.submittedOn === "string" ? saved.submittedOn : null,
          submissionStatus: typeof receipt.status === "string" ? receipt.status : "submitted_pending"}
          : property;
      })}));
      setEdits({});
      setSavedReview(reviewItems);
      setStage("saved");
      setSuccessMessage(`${count} ${count === 1 ? "RSO forecast saved" : "RSO forecasts saved"} (${savedNames}) for ${new Date(month + "-01T12:00:00").toLocaleDateString(undefined, {month:"long", year:"numeric"})}. Waiting for planning. No order has been placed.`);
      retryKey.current = null;
      setReloadKey((value) => value + 1);
    } catch (error) {
      if (!mounted.current) return;
      const described = describeForecastError(
        error,
        "Could not submit these RSO forecasts. Your entries have been preserved."
      );
      if (described.kind === "conflict") {
        setConflictPending(true);
        retryKey.current = null;
        setStage("edit");
        setSelectedPropertyId(String(editState.entries[0].contactInfoId));
        setReloadKey((value) => value + 1);
      }
      setSubmitError(
        described.kind === "general"
          ? { ...described, title: "Unable to submit RSO forecasts" }
          : described
      );
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  return (
    <div className="rsf-widget">
      {/* Until the first input-week read finishes, show a neutral tile, never the pre-input-week one. */}
      {forecastWindow.status === "loading" ? <div className="rsf-button rsf-loading" role="status" aria-label="Loading RSO forecast">
        <span className="rsf-label">RSO forecast</span>
        <span className="rsf-value" aria-hidden="true">…</span>
        <span className="rsf-caption">Loading</span>
      </div> : entryWindow ? <InputWeekTile entryWindow={entryWindow} totals={signInExpired ? null : tileTotals.totals}
        totalsStatus={tileTotals.status} signInExpired={signInExpired}
        forbidden={tileTotals.forbidden === true} forbiddenMessage={tileTotals.forbiddenMessage}
        canViewTeamStatus={forecastWindow.canViewTeamStatus} onRetry={() => forecastWorkspace.refresh()} onOpen={() => {
        // A manager whose own checklist can't load goes straight to the team list.
        setWorkflow(tileTotals.status === "failed" && forecastWindow.canViewTeamStatus ? "team" : "standards");
        setSelectedPropertyId(null);setMonthChoice(windowMonth);
        setStage(hasChanges ? "edit" : "month");setShowDetails(true);
      }} /> : <><button
        type="button"
        className={`rsf-button ${circleState}`}
        onClick={() => {
          setWorkflow(standardsEnabled === false ? "monthly" : "standards");
          setSelectedPropertyId(null);
          setMonthChoice(month);
          setStage(hasChanges ? "edit" : "month");
          setShowDetails(true);
        }}
        title={!legacyCompatibility ? "Properties using RSO materials. Enter each property's expected unit turns every month."
          : standardsEnabled === false ? "Open your monthly RSO forecast" : "View or change RSO materials"}
      >
        <span className="rsf-label">{!legacyCompatibility ? "Monthly RSO unit turns" : standardsEnabled === false ? "RSO forecast" : "RSO materials"}</span>
        <span className="rsf-value">
          {loadStatus === "loading" ? "…" : loaded ? permanentRsoPropertyCount : "—"}
        </span>
        <span className="rsf-caption">{legacyCompatibility ? "your RSO properties" : "RSO properties to enter each month"}</span>
        <span className="rsf-subcount">{!legacyCompatibility ? "Enter unit turns →" : standardsEnabled === false ? "View monthly forecast →" : "View materials →"}</span>
      </button>
      {/* The first read of the week was refused because the sign-in expired. */}
      {signInExpired && <RsoSignInExpired role="status" className="rsf-window-status" />}</>}

      <Modal
        show={showDetails}
        onHide={() => standardsClosePending ? setStandardsClosePending(false) : pendingAction ? keepPendingChanges() : requestAction({kind:"close"})}
        size="xl"
        centered
        scrollable
        backdrop={submitting || standardsBusy || draftNavigationBusy || pendingAction || standardsClosePending ? "static" : true}
        keyboard={!submitting && !standardsBusy && !draftNavigationBusy}
        dialogClassName="rsf-modal"
        aria-labelledby="rsf-modal-title"
      >
        <Modal.Header closeButton={!submitting && !standardsBusy && !draftNavigationBusy && !pendingAction && !standardsClosePending}>
          <Modal.Title id="rsf-modal-title">{standardsClosePending ? "Keep your unsaved material choices?" : pendingAction?.kind === "materials_auto" ? "Keep current material?" : pendingAction?.kind === "zero" ? "Set every count to 0?" : pendingAction?.kind === "month" ? "Switch months and discard your draft?" : pendingAction ? "Keep your unsaved changes?" : workflow === "history" ? "Past RSO requests" : workflow === "overrides" ? "Overrides" : workflow === "team" ? "Team input status" : workflow === "standards" && !legacyCompatibility ? "Property materials" : workflow === "standards" ? "RSO materials" : "Monthly RSO forecast"}</Modal.Title>
        </Modal.Header>
        <Modal.Body ref={bodyRef}>
          {standardsEnabled !== false && (workflow !== "standards" || !standardsNavigation) && !pendingAction && !standardsClosePending && <nav className="rsf-view-options" aria-label="RSO views">
            <Button variant={workflow === "standards" ? "primary" : "outline-primary"}
              aria-pressed={workflow === "standards"} disabled={submitting || standardsBusy || draftNavigationBusy}
              onClick={() => changeWorkflow("standards")}>By property</Button>
            <Button variant={workflow === (legacyCompatibility ? "monthly" : "history") ? "primary" : "outline-primary"}
              aria-pressed={workflow === (legacyCompatibility ? "monthly" : "history")} disabled={submitting || standardsBusy || draftNavigationBusy}
              onClick={() => changeWorkflow(legacyCompatibility ? "monthly" : "history")}>{legacyCompatibility ? "By month" : "Past RSO requests"}</Button>
            {showOverrides && <Button variant={workflow === "overrides" ? "primary" : "outline-primary"}
              aria-pressed={workflow === "overrides"} disabled={submitting || standardsBusy || draftNavigationBusy}
              onClick={() => changeWorkflow("overrides")}>Overrides</Button>}
            {!legacyCompatibility && entryWindow && forecastWindow.canViewTeamStatus && <Button variant={workflow === "team" ? "primary" : "outline-primary"}
              aria-pressed={workflow === "team"} disabled={submitting || standardsBusy || draftNavigationBusy}
              onClick={() => changeWorkflow("team")}>Team input status</Button>}
          </nav>}
          {standardsClosePending && <section>
            <p>{legacyCompatibility ? "Your material choices have not been saved. Keep editing, or discard them to close." : "Your forecast or material choices have not been saved. Keep editing, or discard them to close."}</p>
            <Button variant="outline-danger" onClick={() => {setStandardsSession((value) => value+1);setStandardsDirty(false);setStandardsClosePending(false);
              if (hasChanges) setPendingAction({kind:"close"}); else setShowDetails(false);}}>{legacyCompatibility ? "Discard material changes" : "Discard unsaved choices"}</Button>
            <Button variant="primary" onClick={() => setStandardsClosePending(false)}>Keep editing</Button>
          </section>}
          {draftNavigationError && <Alert variant="danger" role="alert">{draftNavigationError}</Alert>}
          <div hidden={standardsClosePending}>
          <div data-rsf-standards-body hidden={workflow !== "standards" || !!pendingAction}>
            <StandardsTool key={`${token}-${standardsSession}`} properties={serverProperties} addableProperties={data.addableProperties}
              loadDirectory
              onAvailability={handleStandardsAvailability}
              requestedPropertyId={standardsPropertyId}
              active={workflow === "standards" && !pendingAction && !standardsClosePending}
              onNavigationChange={setStandardsNavigation}
              onSessionActionsChange={handleSessionActions}
              onMaterialChangeActionChange={setStandardsMaterialAction}
              token={token} month={month} onDirtyChange={setStandardsDirty} onBusyChange={setStandardsBusy}
              onClose={() => requestAction({kind:"close"})} loading={busy} error={loadError} onReload={() => setReloadKey((value) => value+1)}
              forecastWindow={legacyCompatibility ? undefined : forecastWindow}
              forecastWorkspace={legacyCompatibility ? undefined : forecastWorkspace}
              onOpenOverrides={showOverrides ? () => changeWorkflow("overrides") : undefined} />
          </div>
          {!legacyCompatibility && <div data-rsf-history-body hidden={workflow !== "history" || !!pendingAction}>
            <RsoRequestHistory token={token} active={showDetails && workflow === "history" && !pendingAction && !standardsClosePending} />
          </div>}
          {showOverrides && <div data-rsf-overrides-body hidden={workflow !== "overrides" || !!pendingAction}>
            <RsoOverrideHistory token={token} active={showDetails && workflow === "overrides" && !pendingAction && !standardsClosePending} />
          </div>}
          {!legacyCompatibility && entryWindow && forecastWindow.canViewTeamStatus && <div data-rsf-team-body hidden={workflow !== "team" || !!pendingAction}>
            <RsoTeamInputStatus token={token} entryWindow={entryWindow} active={showDetails && workflow === "team" && !pendingAction && !standardsClosePending} />
          </div>}
          <div data-rsf-monthly-body hidden={workflow !== "monthly" && !pendingAction}>
          {pendingAction?.kind === "materials_auto" ? <p role="status">Use the material already set up for <strong>{pendingAction.propertyName}</strong> instead of your new choice. Your unit counts stay the same.</p>
            : pendingAction?.kind === "zero" ? <p role="status">This will replace every RSO unit count for <strong>{pendingAction.propertyName}</strong> in <strong>{monthLabel(month)}</strong> with 0. {pendingAction.hasCustomMaterials && "Your selected flooring material will stay selected, and its estimate will update. "}You will still review the changes before saving.</p>
            : pendingAction?.kind === "month" ? <p role="status">Your {monthLabel(month)} draft has {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"}. To switch to {monthLabel(pendingAction.month)}, discard that draft.</p>
            : pendingAction && <p role="status">You have {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"} for {monthLabel(month)}. Keep editing to save them, or discard them to close.</p>}
          <div hidden={!!pendingAction}>
          <p className="rsf-context-note">RSO = Recurring Special Order. Enter only this month&apos;s RSO flooring work.</p>
          <ol className="rsf-steps" aria-label="Forecast steps">
            <li aria-current={stage === "month" ? "step" : undefined}>1. Month</li>
            <li aria-current={stage === "edit" ? "step" : undefined}>2. Units &amp; materials</li>
            <li aria-current={stage === "review" || stage === "saved" ? "step" : undefined}>3. Review</li>
          </ol>
          {stage === "month" && <section className="rsf-month-step" aria-label="Choose forecast month">
            <h4 tabIndex="-1">Which month is this RSO forecast for?</h4>
            <p>Count only units that will need RSO materials that month. Leave out other flooring work. This helps plan RSO materials; it does not place an order.</p>
            {entryWindow ? <p className="rsf-month-locked">Forecast month: <strong>{monthLabel(windowMonth)}</strong>. {isWritableMonth(entryWindow, windowMonth)
              ? "Only this month can be entered now."
              : isInputWeek(entryWindow) ? "Entry for this month was closed early."
                : `Entry opens ${shortDate(entryWindow.nextInputWeekStart)} (Mountain Time).`}</p>
              : <Form.Group controlId="rsf-month">
              <Form.Label>Forecast month</Form.Label>
              <Form.Control type="month" value={monthChoice}
                onInput={(event) => setMonthChoice(event.currentTarget.value)}
                onChange={(event) => setMonthChoice(event.target.value)} aria-label="Forecast month" />
            </Form.Group>}
            {hasChanges && <p className="rsf-month-draft-note">{monthChoice !== month && toForecastMonthDate(monthChoice)
              ? `Your unsaved changes are for ${monthLabel(month)}. Switching to ${monthLabel(monthChoice)} will discard that draft.`
              : `Your unsaved changes are for ${monthLabel(month)}. Return to that draft, or choose another month.`}</p>}
            <ForecastError error={loadError} />
            {loadError && token && <Button variant="outline-secondary" onClick={() => setReloadKey((value) => value + 1)}>Refresh</Button>}
          </section>}
          {stage === "review" && <>
            <ForecastReview items={reviewItems} month={month} />
            <ForecastError error={submitError} />
            <ForecastError error={loadError} />
          </>}
          {stage === "saved" && <>
            {successMessage && <Alert variant="success" role="status">{successMessage}</Alert>}
            <ForecastReview items={savedReview} month={month} saved />
            {loadError && <Alert variant="warning">Your save was confirmed, but the latest property list could not be refreshed. Open the forecasts and reload before making more changes.</Alert>}
          </>}
          {stage === "edit" && <>
          <div className="rsf-month-banner">
            <div><span>Forecast month:</span><strong>{monthLabel(month)}</strong></div>
            <Button variant="link" onClick={openMonthChoice}>Change month</Button>
          </div>

          <ForecastError error={loadError} />
          <ForecastError error={submitError} />
          {conflictPending && !submitError && <p role="status">Your draft is safe. Review it against the latest saved forecast before saving again.</p>}
          {!addingProperty && eligibleDirtyEdits.length > 0 && <section className="rsf-drafts" aria-label="Unsaved changes">
            <h5>Unsaved changes ({eligibleDirtyEdits.length})</h5>
            <p>These changes are not saved yet. You can keep working on another property.</p>
            <ul>{eligibleDirtyEdits.map((entry) => {
              const property = eligibleProperties.find((item) => String(item.contactInfoId) === String(entry.contactInfoId));
              const label = propertyLabel(property);
              const invalid = editState.invalid.some((item) => String(item.contactInfoId) === String(entry.contactInfoId));
              return <li key={entry.contactInfoId}>
                <Button variant="link" disabled={busy} aria-label={`Open unsaved changes for ${label}`}
                  onClick={() => chooseProperty(entry.contactInfoId, true)}>{label}</Button>
                <span>{invalid ? "Check counts or materials" : "Ready to review"}</span>
              </li>;
            })}</ul>
          </section>}
          {!addingProperty && editState.invalid.length > 0 && <Alert variant="warning">
            <Alert.Heading>Check these counts or materials</Alert.Heading>
            <p>Check the highlighted entries. Use whole numbers for units and choose an RSO material for each added property. For an older forecast, use Change material to choose each material again.</p>
            {editState.invalid.map((entry) => <Button key={entry.contactInfoId} variant="link" disabled={busy}
              onClick={() => chooseProperty(entry.contactInfoId, true)}>{entry.propertyName} — check counts or materials</Button>)}
          </Alert>}
          {!addingProperty && loadStatus === "ok" && unavailableChanges.length > 0 && <Alert variant="warning">
            <Alert.Heading>{unavailableChanges.length === 1 ? "One draft is no longer available" : `${unavailableChanges.length} drafts are no longer available`}</Alert.Heading>
            <p>{unavailableChanges.length === 1 ? "Its property is" : "Their properties are"} no longer assigned to you or no longer use RSO materials. The {unavailableChanges.length === 1 ? "draft was" : "drafts were"} not saved. Contact Sales Support or discard {unavailableChanges.length === 1 ? "it" : "them"} before saving another forecast.</p>
            <Button variant="outline-secondary" onClick={() => setEdits((previous) => Object.fromEntries(Object.entries(previous).filter(([key]) => !unavailableChanges.some((entry) => String(entry.contactInfoId) === key))))}>Discard unavailable {unavailableChanges.length === 1 ? "draft" : "drafts"}</Button>
          </Alert>}

          {selectedProperty ? <>
            <div className="rsf-editor-toolbar">
              <Button variant="outline-secondary" onClick={chooseAnotherProperty}>Change property</Button>
              <Button variant="link" disabled={loadStatus === "loading" || loadStatus === "refreshing"} onClick={() => setReloadKey((value) => value + 1)}>{loadStatus === "refreshing" ? "Refreshing…" : "Refresh"}</Button>
            </div>
            {!legacyEditable ? <section aria-label="Previous saved forecast"><h5>{selectedProperty.propertyName}</h5><p>This forecast keeps its original calculation. Enter new forecasts using By property.</p><SavedManualForecast property={selectedProperty} month={month} historical /><RsoChangeHistory property={selectedProperty} month={month} token={token} reloadKey={reloadKey} /></section> : <PropertyForecast key={selectedProperty.contactInfoId} property={selectedProperty} edit={edits[String(selectedProperty.contactInfoId)]} month={month}
              token={token} reloadKey={reloadKey}
              onChange={handleForecastChange} onZero={handleZero} onUndo={handleUndo} onMaterialChange={handleMaterialChange}
              onCalculated={(property) => setPendingAction({kind:"materials_auto",contactInfoId:property.contactInfoId,propertyName:property.propertyName})}
              onChangeStandard={standardsEnabled === false ? null : openMaterialStandards} submitting={busy} />}
          </> : addingProperty ? <section className="rsf-add-property" aria-label="Add a property for this month">
            <div className="rsf-intro">
              <h4 tabIndex="-1">Add a property</h4>
              <p>Choose a property that is missing from your RSO list. Only properties assigned to you are shown. Its current material does not have to be RSO.</p>
            </div>
            <p className="rsf-manual-exception-note"><strong>This is for {monthLabel(month)} only.</strong> It does not permanently mark the property or its flooring as RSO. You will choose an RSO material before you can review and save.</p>
            <Form.Group className="rsf-search-field" controlId="rsf-candidate-search">
              <Form.Label>Search properties you can add</Form.Label>
              <Form.Control type="search" value={candidateQuery} placeholder="Property name, store, or CoStar ID"
                aria-label="Search properties you can add"
                onChange={(event) => {setCandidateQuery(event.target.value);setCandidatePage(1);}} />
            </Form.Group>
            <div className="rsf-results-heading"><span>{filteredCandidates.length} {filteredCandidates.length === 1 ? "property" : "properties"} available{candidateQuery ? " for “" + candidateQuery + "”" : ""}</span></div>
            {filteredCandidates.length === 0 ? <div className="rsf-state">
              {candidateQuery ? `No properties you can add match “${candidateQuery}”.` : "No other assigned properties can be added for this month."}
              {candidateQuery && <div className="rsf-empty-actions"><Button variant="link" onClick={() => {setCandidateQuery("");setCandidatePage(1);}}>Clear search</Button></div>}
            </div> : <div className="rsf-properties">{visibleCandidates.map((property,index) => <AddPropertyChoice
              key={`${property.contactInfoId}-${index}`} property={property} label={candidateLabel(property)} month={month}
              onAdd={addProperty} disabled={busy} />)}</div>}
            {candidateTotalPages > 1 && <div className="rsf-pagination">
              <Button variant="outline-secondary" size="sm" disabled={currentCandidatePage <= 1 || submitting}
                onClick={() => setCandidatePage(Math.max(1,currentCandidatePage - 1))}>Previous</Button>
              <span>Page {currentCandidatePage} of {candidateTotalPages}</span>
              <Button variant="outline-secondary" size="sm" disabled={currentCandidatePage >= candidateTotalPages || submitting}
                onClick={() => setCandidatePage(currentCandidatePage + 1)}>Next</Button>
            </div>}
          </section> : <>
          <div className="rsf-intro">
            <h4 tabIndex="-1">Your RSO properties</h4>
            <p>Your assigned properties that normally use RSO materials are shown here.</p>
            <p><strong>Missing a property?</strong> Use Add property to include one of your other properties for this month.</p>
          </div>
          <div className="rsf-toolbar">
            <Form.Group className="rsf-search-field" controlId="rsf-search">
              <Form.Label>Search your RSO properties</Form.Label>
              <Form.Control type="search" value={query} placeholder="Property name, store, or CoStar ID" aria-label="Search your RSO properties"
                onChange={(event) => {setQuery(event.target.value); setPage(1);}} />
            </Form.Group>
            <div className="rsf-toolbar-actions">
              {legacyCompatibility && <Button variant="primary" onClick={openAddProperty} disabled={busy}>Add property</Button>}
              <Button
                variant="outline-secondary"
                onClick={() => setReloadKey((value) => value + 1)}
                disabled={loadStatus === "loading" || loadStatus === "refreshing" || submitting}
              >
                {loadStatus === "refreshing" ? "Refreshing…" : "Refresh list"}
              </Button>
            </div>
          </div>

          {payload && <>
            <div className="rsf-results-heading"><span>{filtered.length} RSO {filtered.length === 1 ? "property" : "properties"}{query ? " for “" + query + "”" : ""}</span>
              <span>Saved this month: {counts.saved}</span>
            </div>
          </>}

          {loadStatus === "loading" ? (
            <div className="rsf-state">
              <Spinner animation="border" size="sm" role="status" /> Loading forecasts…
            </div>
          ) : filtered.length === 0 && !loadError ? (
            <div className="rsf-state">
              {query ? `No RSO properties match “${query}”.` : "No RSO properties are assigned to you for this month. If you expected to see one, contact Sales Support."}
              {query && <div className="rsf-empty-actions">
                <Button variant="link" onClick={() => {setQuery(""); setPage(1);}}>Clear search</Button>
              </div>}
            </div>
          ) : (
            <div className="rsf-properties">
              {visibleProperties.map((property, index) => (
                <PropertyChoice
                  key={`${property.contactInfoId ?? "property"}-${index}`}
                  property={property}
                  edit={edits[String(property.contactInfoId)]}
                  label={propertyLabel(property)}
                  onChoose={chooseProperty}
                  disabled={busy}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="rsf-pagination">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={currentPage <= 1 || submitting}
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              >
                Previous
              </Button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={currentPage >= totalPages || submitting}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
          </>}
          </>}
          </div>
          </div>
          </div>
        </Modal.Body>
        {workflow === "standards" && !pendingAction && !standardsClosePending && <Modal.Footer>
          {standardsMaterialAction && <div className="rpf-footer-send">
            <span>{standardsMaterialAction.name}</span>
            {standardsMaterialAction.run && <Button variant="success" className="rpf-send-purchasing" disabled={standardsMaterialAction.disabled || draftNavigationBusy}
              onClick={standardsMaterialAction.run}>{standardsMaterialAction.sending ? "Sending…" : "Send to Purchasing"}</Button>}
            {standardsMaterialAction.discard && <Button variant="outline-danger" disabled={standardsMaterialAction.disabled || draftNavigationBusy}
              onClick={standardsMaterialAction.discard}>Discard change</Button>}
          </div>}
          {standardsNavigation && <Button variant="outline-secondary" disabled={standardsBusy || draftNavigationBusy} onClick={standardsNavigation.action}>{standardsNavigation.label}</Button>}
          <Button variant="outline-secondary" disabled={standardsBusy || draftNavigationBusy} onClick={() => requestAction({kind:"close"})}>{legacyCompatibility ? "Close" : draftNavigationBusy ? "Saving draft…" : "Save and exit"}</Button>
        </Modal.Footer>}
        {(workflow === "history" || workflow === "overrides" || workflow === "team") && !pendingAction && !standardsClosePending && <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => requestAction({kind:"close"})}>Close</Button>
        </Modal.Footer>}
        {(workflow === "monthly" || pendingAction) && !standardsClosePending && <Modal.Footer>
          {pendingAction?.kind === "materials_auto" ? <>
            <Button variant="outline-danger" disabled={busy} onClick={confirmCalculatedMaterials}>Keep current material</Button>
            <Button variant="primary" ref={keepChangesRef} onClick={keepPendingChanges}>Keep selected materials</Button>
          </> : pendingAction?.kind === "zero" ? <>
            <Button variant="outline-danger" disabled={busy} onClick={confirmZero}>Set counts to 0</Button>
            <Button variant="primary" ref={keepChangesRef} onClick={keepPendingChanges}>Keep counts</Button>
          </> : pendingAction?.kind === "month" ? <>
            <Button variant="outline-danger" onClick={discardAndContinue}>Discard {monthLabel(month)} draft and switch to {monthLabel(pendingAction.month)}</Button>
            <Button variant="primary" ref={keepChangesRef} onClick={keepPendingChanges}>Back to {monthLabel(month)} draft</Button>
          </> : pendingAction ? <>
            <Button variant="outline-danger" onClick={discardAndContinue}>Discard changes</Button>
            <Button variant="primary" ref={keepChangesRef} onClick={keepPendingChanges}>Keep editing</Button>
          </> : stage === "month" ? <>
            <Button variant="outline-secondary" onClick={() => requestAction({kind:"close"})}>Close</Button>
            {hasChanges ? <>
              {monthChoice !== month && toForecastMonthDate(monthChoice) && <Button variant="outline-danger"
                onClick={() => requestAction({kind:"month",month:monthChoice})}>Discard {monthLabel(month)} draft and switch to {monthLabel(monthChoice)}</Button>}
              <Button variant="primary" onClick={() => {setMonthChoice(month);setStage("edit");}}>Back to {monthLabel(month)} draft</Button>
            </> : <Button variant="primary" disabled={!token || !toForecastMonthDate(monthChoice)} onClick={confirmMonth}>Continue with {monthLabel(monthChoice)}</Button>}
          </> : stage === "saved" ? <>
            <Button variant="outline-secondary" onClick={() => {chooseAnotherProperty();setSuccessMessage("");}}>Back to forecasts</Button>
            <Button variant="primary" onClick={() => setShowDetails(false)}>Done</Button>
          </> : stage === "review" ? <>
            <Button variant="outline-secondary" disabled={submitting} onClick={() => setStage("edit")}>Back to edit</Button>
            <Button variant="primary" disabled={cannotReview} onClick={handleSubmit}>{submitting ? "Saving…" : "Save RSO forecast"}</Button>
          </> : addingProperty ? <>
            <Button variant="outline-secondary" onClick={() => setAddingProperty(false)}>Back to properties</Button>
            <Button variant="outline-secondary" onClick={() => requestAction({kind:"close"})}>Close</Button>
          </> : <>
          <Button
            variant="outline-secondary"
            disabled={submitting}
            onClick={selectedProperty ? chooseAnotherProperty : openMonthChoice}
          >
            {selectedProperty ? "Back to properties" : "Back to month"}
          </Button>
          <div className="rsf-submit-summary" aria-live="polite">
            {unavailableChanges.length > 0 ? "Review the unavailable edits above before saving."
              : editState.invalid.length > 0
              ? "Check the highlighted counts or property changes before reviewing."
              : editState.entries.length > 0
              ? `${editState.entries.length} changed forecast${
                  editState.entries.length === 1 ? "" : "s"
                } to review.`
              : "No changes."}
          </div>
          <Button variant="outline-secondary" onClick={() => requestAction({kind:"close"})} disabled={submitting}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (conflictPending) {
                setEdits((previous) => Object.fromEntries(Object.entries(previous).map(([key,entry]) => {
                  const latest = eligibleProperties.find((property) => String(property.contactInfoId) === key);
                  return [key,latest?.forecastRevision !== undefined ? {...entry,expectedRevision:latest.forecastRevision} : entry];
                })));
                setConflictPending(false);
                setSubmitError(null);
              }
              setStage("review");
            }}
            disabled={cannotReview}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" aria-hidden="true" /> Saving…
              </>
            ) : (
              "Review forecast"
            )}
          </Button>
          </>}
        </Modal.Footer>}
      </Modal>
    </div>
  );
};

// Retain the previous editor for compatibility checks and pre-migration draft support.
export const LegacyRsoSalesForecast = ({auth}) => {
  const token = auth?.auth?.[0]?.token || null;
  return <ForecastSession key={token || "signed-out"} token={token} legacyCompatibility />;
};

// A new sign-in must never inherit another account's properties or drafts.
// The RSO switch (Admin page) is read first: while it is off the tile is not shown at all and no
// other RSO read is made (NANCY also refuses every Sales RSO request then). Only a clear "off"
// hides it: if the switch can't be read, the tile shows as usual.
export const RsoSalesForecast = ({auth}) => {
  const token = auth?.auth?.[0]?.token || null;
  const rsoSwitch = useRsoSwitch({token});
  if (rsoSwitch.value?.enabled === false) return null;
  // The same neutral tile ForecastSession shows while its first read is in flight.
  if (rsoSwitch.loading) return <div className="rsf-widget"><div className="rsf-button rsf-loading" role="status" aria-label="Loading RSO forecast">
    <span className="rsf-label">RSO forecast</span>
    <span className="rsf-value" aria-hidden="true">…</span>
    <span className="rsf-caption">Loading</span>
  </div></div>;
  return <ForecastSession key={token || "signed-out"} token={token} />;
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(RsoSalesForecast);
